import {
  getTrackRightsSplits,
  replaceTrackRightsSplits,
  bulkSaveRightsSplits,
} from '@disco/api/rights-data'
import type {
  RightsSplitChain,
  RightsType,
  ReplaceRightsSplitsRequest,
  BulkRightsSplitsRequest,
  BulkRightsSplitsResponse,
} from 'types/rights-data'

import { api } from './index'

export interface GetTrackRightsSplitsParams {
  trackId: number
  rightsType?: RightsType
  territory?: string
}

export const rightsSplitsApi = api
  .enhanceEndpoints({ addTagTypes: ['RightsSplits'] })
  .injectEndpoints({
    endpoints: build => ({
      getTrackRightsSplits: build.query<
        RightsSplitChain[],
        GetTrackRightsSplitsParams
      >({
        queryFn: async ({ trackId, rightsType, territory }) => {
          try {
            const results = await getTrackRightsSplits(
              trackId,
              rightsType,
              territory,
            )
            return { data: results }
          } catch (error) {
            return { error }
          }
        },
        providesTags: (result, _error, { trackId, rightsType }) =>
          result
            ? [
                ...result.map(chain => ({
                  type: 'RightsSplits' as const,
                  id: `${trackId}-${chain.id}`,
                })),
                {
                  type: 'RightsSplits' as const,
                  id: `TRACK-${trackId}-${rightsType ?? 'all'}`,
                },
              ]
            : [
                {
                  type: 'RightsSplits' as const,
                  id: `TRACK-${trackId}-${rightsType ?? 'all'}`,
                },
              ],
      }),
      replaceTrackRightsSplits: build.mutation<
        RightsSplitChain[],
        { trackId: number; data: ReplaceRightsSplitsRequest }
      >({
        queryFn: async ({ trackId, data }) => {
          try {
            const results = await replaceTrackRightsSplits(trackId, data)
            return { data: results }
          } catch (error) {
            return { error }
          }
        },
        invalidatesTags: (_result, _error, { trackId }) => [
          { type: 'RightsSplits', id: `TRACK-${trackId}-publishing` },
          { type: 'RightsSplits', id: `TRACK-${trackId}-recording` },
          { type: 'RightsSplits', id: `TRACK-${trackId}-all` },
        ],
      }),
      bulkSaveRightsSplits: build.mutation<
        BulkRightsSplitsResponse,
        { data: BulkRightsSplitsRequest; atomic?: boolean }
      >({
        queryFn: async ({ data, atomic = true }) => {
          try {
            const results = await bulkSaveRightsSplits(data, atomic)
            return { data: results }
          } catch (error) {
            return { error }
          }
        },
        invalidatesTags: (_result, _error, { data }) => {
          // Normalize both shorthand and full formats into (trackId, rightsType) pairs.
          const pairs =
            'tracks' in data
              ? data.tracks.map(t => ({
                  trackId: t.track_id,
                  rightsType: t.rights_type,
                }))
              : data.track_ids.map(trackId => ({
                  trackId,
                  rightsType: data.rights_type,
                }))

          return pairs.flatMap(({ trackId, rightsType }) => [
            {
              type: 'RightsSplits' as const,
              id: `TRACK-${trackId}-${rightsType}`,
            },
            {
              type: 'RightsSplits' as const,
              id: `TRACK-${trackId}-all`,
            },
          ])
        },
      }),
    }),
  })

export const {
  useGetTrackRightsSplitsQuery,
  useReplaceTrackRightsSplitsMutation,
  useBulkSaveRightsSplitsMutation,
} = rightsSplitsApi
